// CTSK-292 — per-tool manifest modal (View / Edit / Download / Copy).
//
// Mirrors the DocumentYamlEditModal (CTSK-268 slice B) shape: the
// operator works on the RAW formatted JSON text — no client-side
// re-serialization games — and the save replaces the manifest through
// PATCH /tools/:id/manifest with If-Match single-writer safety.
//
// Locking: default LOCKED (read-only textarea). The EditLockToggle in
// the header (rendered only for tools:configure holders) flips edit
// mode; opening via the row's Edit button starts unlocked. Save sends
// the full manifest; the server freezes identity fields (slug / type /
// nest / container / actions) and 400s with *_CHANGE_FORBIDDEN codes —
// surfaced via toast.fromError.
//
// Conflict flow: a 412 PRECONDITION_FAILED (or the older 409 CONFLICT
// shape documents use) keeps the modal OPEN, keeps the operator's
// edits in the textarea, and shows reload-before-overwriting guidance
// with an explicit "Load latest" affordance. No optimistic update —
// the tools list only refreshes after a confirmed save.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiSingle } from '../lib/api.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { toast } from './Toast.js';
import { EditLockToggle } from './EditLockToggle.js';

export interface ManifestPayload {
  toolId: string;
  manifest: Record<string, unknown>;
  etag: string;
  changed?: string[];
  warnings?: string[];
}

/** Browser-side "save as file" for a text payload. Shared by the
 *  modal's Download button and the ToolsPanel per-row Download. */
export function downloadTextFile(filename: string, text: string) {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ToolManifestModalProps {
  toolId: string;
  toolName: string;
  toolSlug: string;
  toolVersion: string;
  /** 'view' opens locked; 'edit' opens unlocked (when canConfigure). */
  mode: 'view' | 'edit';
  /** Caller-resolved tools:configure check — hides the lock toggle and
   *  Save entirely when false; the server cap gate stays authoritative. */
  canConfigure: boolean;
  onClose: () => void;
}

export function ToolManifestModal({
  toolId,
  toolName,
  toolSlug,
  toolVersion,
  mode,
  canConfigure,
  onClose,
}: ToolManifestModalProps) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [locked, setLocked] = useState(!(mode === 'edit' && canConfigure));
  const [conflict, setConflict] = useState<string | null>(null);

  const manifestQ = useQuery({
    queryKey: ['tool-manifest', toolId],
    queryFn: () => apiSingle<ManifestPayload>(`/tools/${toolId}/manifest`),
  });

  // Seed the textarea ONCE from the first successful fetch — later
  // refetches (e.g. after "Load latest") replace it explicitly instead,
  // so a background refetch can never clobber in-flight edits.
  useEffect(() => {
    if (!initialized && manifestQ.data) {
      setText(JSON.stringify(manifestQ.data.manifest, null, 2));
      setInitialized(true);
    }
  }, [initialized, manifestQ.data]);

  const saveMut = useMutation({
    mutationFn: () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch (err) {
        return Promise.reject(
          new Error(`Manifest is not valid JSON: ${(err as Error).message}`),
        );
      }
      return apiSingle<ManifestPayload>(`/tools/${toolId}/manifest`, {
        method: 'PATCH',
        body: { manifest: parsed },
        ifMatch: manifestQ.data?.etag,
      });
    },
    onSuccess: (result) => {
      toast.success(`${toolSlug} manifest saved`);
      if (result.warnings && result.warnings.length > 0) {
        toast.info(result.warnings.join(' • '));
      }
      qc.invalidateQueries({ queryKey: ['tools'] });
      qc.invalidateQueries({ queryKey: ['tool-manifest', toolId] });
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && (err.status === 412 || err.status === 409)) {
        setConflict(
          `${toolSlug} changed since you loaded it. Reload the latest manifest before overwriting — your edits stay in the textarea until you do.`,
        );
        return;
      }
      if (err instanceof Error && err.message.startsWith('Manifest is not valid JSON')) {
        toast.error(err.message);
        return;
      }
      toast.fromError(err, `Failed to save ${toolSlug} manifest`);
    },
  });

  const loadLatest = async () => {
    const fresh = await manifestQ.refetch();
    if (fresh.data) {
      setText(JSON.stringify(fresh.data.manifest, null, 2));
      setConflict(null);
      toast.info(`Loaded the latest ${toolSlug} manifest — re-apply your edit.`);
    }
  };

  const handleDownload = () => {
    downloadTextFile(`${toolSlug}.manifest.json`, text);
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(text);
    if (ok) toast.success('Manifest JSON copied to clipboard');
    else toast.error('Copy failed — select the text and copy manually.');
  };

  // ESC closes (unless a save is in flight).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saveMut.isPending) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, saveMut.isPending]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Manifest of ${toolName}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          background: 'var(--surface-raised)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          width: 'min(860px, 100%)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 16,
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            {toolName}{' '}
            <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, background: 'var(--surface-sunken)', padding: '1px 5px', borderRadius: 4 }}>
              {toolSlug}
            </code>{' '}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
              v{toolVersion}
            </span>
          </h2>
          {canConfigure && (
            <EditLockToggle locked={locked} onToggle={() => setLocked((l) => !l)} />
          )}
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Effective current manifest as formatted JSON — download or copy to
          paste-install into another org. Identity fields (slug, type, nest,
          container, actions) are frozen post-install.
        </span>
        {conflict && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '8px 10px',
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid var(--warning-primary, #A05A00)',
              color: 'var(--warning-primary, #A05A00)',
              background: 'color-mix(in srgb, var(--warning-primary, #A05A00) 8%, var(--surface-raised))',
            }}
          >
            <span>{conflict}</span>
            <button
              type="button"
              onClick={() => void loadLatest()}
              style={{ padding: '4px 10px', fontSize: 12, border: '1px solid var(--warning-primary, #A05A00)', borderRadius: 6, background: 'transparent', color: 'var(--warning-primary, #A05A00)', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Load latest
            </button>
          </div>
        )}
        {manifestQ.isLoading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>
            Loading manifest…
          </div>
        ) : manifestQ.isError ? (
          <div style={{ color: 'var(--danger-primary, #B3261E)', fontSize: 13, padding: '24px 0' }}>
            Failed to load the manifest.
          </div>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            readOnly={locked}
            aria-label={`Manifest JSON of ${toolSlug}`}
            spellCheck={false}
            style={{
              flex: 1,
              minHeight: '50vh',
              resize: 'vertical',
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              lineHeight: 1.5,
              padding: 10,
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              background: 'var(--surface-sunken)',
              color: locked ? 'var(--text-secondary)' : 'var(--text-primary)',
              whiteSpace: 'pre',
            }}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!initialized}
            style={{ padding: '6px 14px', background: 'var(--surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
          >
            Download
          </button>
          <button
            type="button"
            onClick={() => void handleCopy()}
            disabled={!initialized}
            style={{ padding: '6px 14px', background: 'var(--surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
          >
            Copy to clipboard
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saveMut.isPending}
            style={{ padding: '6px 14px', background: 'var(--surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
          {canConfigure && !locked && (
            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending || !initialized}
              style={{ padding: '6px 14px', background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saveMut.isPending || !initialized ? 0.6 : 1 }}
            >
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
